// src/components/resumo/annotations/useAnnotations.ts
// Hook principal para gerenciar anotações com histórico de undo/redo

import { useState, useEffect, useCallback, useRef } from 'react';
import { AnnotationData, AnnotationTool, Comment, Highlight, Note } from './annotations.types';

const AUTOSAVE_KEY_PREFIX = 'graia_autosave_annotations_';
const AUTOSAVE_INTERVAL = 4000; // 4 segundos

function makeEmptyAnnotations(userId: string, deckId: string, subdeckId: string): AnnotationData {
    return {
        userId,
        deckId,
        subdeckId,
        highlights: [],
        notes: [],
        comments: [],
        lastModified: new Date().toISOString()
    };
}

export interface AutosaveInfo {
    savedAt: string;
    data: AnnotationData;
}

export const useAnnotations = (userId: string, deckId: string, subdeckId: string) => {
    const [annotations, setAnnotationsRaw] = useState<AnnotationData>(
        makeEmptyAnnotations(userId, deckId, subdeckId)
    );

    // ====== HISTÓRICO UNDO/REDO ======
    const historyRef = useRef<AnnotationData[]>([]);
    const historyIndexRef = useRef<number>(-1);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const skipHistoryRef = useRef(false); // evita push ao restaurar

    const pushHistory = useCallback((state: AnnotationData) => {
        if (skipHistoryRef.current) return;
        const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
        // Limitar histórico a 50 estados
        if (newHistory.length >= 50) newHistory.shift();
        newHistory.push(state);
        historyRef.current = newHistory;
        historyIndexRef.current = newHistory.length - 1;
        setCanUndo(historyIndexRef.current > 0);
        setCanRedo(false);
    }, []);

    const setAnnotations = useCallback((updater: AnnotationData | ((prev: AnnotationData) => AnnotationData)) => {
        setAnnotationsRaw(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            pushHistory(next);
            return next;
        });
    }, [pushHistory]);

    const undo = useCallback(() => {
        if (historyIndexRef.current <= 0) return;
        historyIndexRef.current -= 1;
        const state = historyRef.current[historyIndexRef.current];
        skipHistoryRef.current = true;
        setAnnotationsRaw(state);
        skipHistoryRef.current = false;
        setCanUndo(historyIndexRef.current > 0);
        setCanRedo(true);
    }, []);

    const redo = useCallback(() => {
        if (historyIndexRef.current >= historyRef.current.length - 1) return;
        historyIndexRef.current += 1;
        const state = historyRef.current[historyIndexRef.current];
        skipHistoryRef.current = true;
        setAnnotationsRaw(state);
        skipHistoryRef.current = false;
        setCanUndo(true);
        setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
    }, []);

    const [activeTool, setActiveTool] = useState<AnnotationTool>(null);
    const [highlightMode, setHighlightMode] = useState(false);
    const [noteMode, setNoteMode] = useState(false);

    const toggleHighlightMode = useCallback(() => {
        setHighlightMode(prev => !prev);
        setNoteMode(false);
        setActiveTool(highlightMode ? null : 'highlight');
    }, [highlightMode]);

    const toggleNoteMode = useCallback(() => {
        setNoteMode(prev => !prev);
        setHighlightMode(false);
        setActiveTool(noteMode ? null : 'note');
    }, [noteMode]);

    // ====== AUTOSAVE LOCAL ======
    const autosaveKey = userId && deckId && subdeckId
        ? `${AUTOSAVE_KEY_PREFIX}${userId}_${deckId}_${subdeckId}`
        : null;

    const saveAutosave = useCallback((data: AnnotationData) => {
        if (!autosaveKey) return;
        try {
            const payload: AutosaveInfo = { savedAt: new Date().toISOString(), data };
            localStorage.setItem(autosaveKey, JSON.stringify(payload));
        } catch { /* quota exceeded — ignore */ }
    }, [autosaveKey]);

    const clearAutosave = useCallback(() => {
        if (!autosaveKey) return;
        localStorage.removeItem(autosaveKey);
    }, [autosaveKey]);

    const getAutosaveInfo = useCallback((): AutosaveInfo | null => {
        if (!autosaveKey) return null;
        try {
            const raw = localStorage.getItem(autosaveKey);
            if (!raw) return null;
            return JSON.parse(raw) as AutosaveInfo;
        } catch { return null; }
    }, [autosaveKey]);

    // Autosave periódico — usa annotationsRef para leitura síncrona
    const autosaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    useEffect(() => {
        autosaveTimerRef.current = setInterval(() => {
            saveAutosave(annotationsRef.current);
        }, AUTOSAVE_INTERVAL);
        return () => {
            if (autosaveTimerRef.current) clearInterval(autosaveTimerRef.current);
        };
    }, [saveAutosave]);

    // Autosave ao fechar/sair da página (visibilitychange + beforeunload)
    useEffect(() => {
        const flush = () => saveAutosave(annotationsRef.current);
        const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('beforeunload', flush);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('beforeunload', flush);
        };
    }, [saveAutosave]);

    // ====== CARREGAR ANOTAÇÕES ======
    const loadAnnotations = useCallback(async () => {
        try {
            const { annotationService } = await import('../../../services/annotation.service');
            const data = await annotationService.load(userId, deckId, subdeckId);
            if (data) {
                const normalized = { ...data, comments: data.comments || [] };
                // Inicializar histórico com o estado carregado
                skipHistoryRef.current = true;
                setAnnotationsRaw(normalized);
                historyRef.current = [normalized];
                historyIndexRef.current = 0;
                setCanUndo(false);
                setCanRedo(false);
                skipHistoryRef.current = false;
            }
        } catch (error) {
            console.error('Erro ao carregar anotações:', error);
        }
    }, [userId, deckId, subdeckId]);

    useEffect(() => {
        if (userId && deckId && subdeckId) {
            loadAnnotations();
        }
    }, [userId, deckId, subdeckId, loadAnnotations]);

    // ====== HIGHLIGHTS ======
    const addHighlight = useCallback((highlight: Highlight) => {
        setAnnotations(prev => ({
            ...prev,
            highlights: [...prev.highlights, highlight],
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const removeHighlight = useCallback((id: string) => {
        setAnnotations(prev => ({
            ...prev,
            highlights: prev.highlights.filter(h => h.id !== id),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    // ====== NOTES ======
    const addNote = useCallback((note: Note) => {
        setAnnotations(prev => ({
            ...prev,
            notes: [...prev.notes, note],
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const updateNoteComplete = useCallback((id: string, data: Partial<Note>) => {
        setAnnotations(prev => ({
            ...prev,
            notes: prev.notes.map(n =>
                n.id === id ? { ...n, ...data, updatedAt: new Date().toISOString() } : n
            ),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const updateHighlightNote = useCallback((highlightId: string, note: string) => {
        setAnnotations(prev => ({
            ...prev,
            highlights: prev.highlights.map(h =>
                h.id === highlightId ? { ...h, note } : h
            ),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const updateNote = useCallback((id: string, text: string) => {
        setAnnotations(prev => ({
            ...prev,
            notes: prev.notes.map(n =>
                n.id === id ? { ...n, text, updatedAt: new Date().toISOString() } : n
            ),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const removeNote = useCallback((id: string) => {
        setAnnotations(prev => ({
            ...prev,
            notes: prev.notes.filter(n => n.id !== id),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const clearAllDrawings = useCallback(() => {
        setAnnotations(prev => ({
            ...prev,
            drawings: [],
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    // ====== COMMENTS ======
    const addComment = useCallback((comment: Comment) => {
        setAnnotations(prev => ({
            ...prev,
            comments: [...(prev.comments || []), comment],
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const removeComment = useCallback((id: string) => {
        setAnnotations(prev => ({
            ...prev,
            comments: (prev.comments || []).filter(c => c.id !== id),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    const bulkUpdateHighlights = useCallback((pageId: string, updated: Highlight[]) => {
        setAnnotations(prev => ({
            ...prev,
            highlights: [
                ...prev.highlights.filter(h => h.pageId !== pageId),
                ...updated,
            ],
            lastModified: new Date().toISOString(),
        }));
    }, [setAnnotations]);

    const updateComment = useCallback((id: string, data: Partial<Comment>) => {
        setAnnotations(prev => ({
            ...prev,
            comments: (prev.comments || []).map(c =>
                c.id === id ? { ...c, ...data } : c
            ),
            lastModified: new Date().toISOString()
        }));
    }, [setAnnotations]);

    // Ref para acessar estado atual de forma síncrona no save
    const annotationsRef = useRef<AnnotationData>(makeEmptyAnnotations(userId, deckId, subdeckId));
    useEffect(() => { annotationsRef.current = annotations; }, [annotations]);

    // ====== SALVAR ======
    // Guarda defensiva: sem userId/deckId/subdeckId (ex: visitante anônimo
    // numa prévia pública) não há conta nem apostila real pra associar a
    // anotação — nunca deve tentar gravar em nome de "ninguém". Mesmo
    // critério já usado em autosaveKey/loadAnnotations acima.
    const saveAnnotations = useCallback(async () => {
        if (!userId || !deckId || !subdeckId) {
            return { success: false, message: 'Não é possível salvar anotações nesta visualização.' };
        }
        try {
            const { annotationService } = await import('../../../services/annotation.service');
            await annotationService.save(annotationsRef.current);
            clearAutosave(); // limpa autosave após salvar com sucesso
            return { success: true, message: 'Anotações salvas com sucesso!' };
        } catch (error) {
            console.error('Erro ao salvar:', error);
            return { success: false, message: 'Erro ao salvar anotações' };
        }
    }, [userId, deckId, subdeckId, clearAutosave]);

    return {
        annotations,
        activeTool,
        setActiveTool,

        highlightMode,
        noteMode,
        toggleHighlightMode,
        toggleNoteMode,

        // Undo/Redo
        undo,
        redo,
        canUndo,
        canRedo,

        // Autosave
        getAutosaveInfo,
        clearAutosave,

        updateNoteComplete,
        updateHighlightNote,

        addHighlight,
        removeHighlight,
        bulkUpdateHighlights,
        addNote,
        updateNote,
        removeNote,
        clearAllDrawings,
        addComment,
        removeComment,
        updateComment,
        saveAnnotations,
        loadAnnotations
    };
};